<template>
  <section class="sale-payment-plan-tab">
    <ConfirmActionModal
      :show="isDeleteConfirmModalOpen"
      :title="text.tab.confirms.delete.title"
      :subtitle="text.tab.confirms.delete.subtitle"
      :message="deleteConfirmMessage"
      :confirm-text="deleteConfirmText"
      :cancel-text="text.tab.confirms.delete.cancel"
      @cancel="closeDeleteConfirmModal"
      @confirm="confirmDeletePlan"
    />

    <ConfirmActionModal
      :show="isClearConfirmModalOpen"
      :title="text.tab.confirms.clear.title"
      :subtitle="text.tab.confirms.clear.subtitle"
      :message="clearConfirmMessage"
      :confirm-text="text.tab.confirms.clear.confirm"
      @cancel="closeClearConfirmModal"
      @confirm="confirmClearPaymentPlans"
    />

    <SalePaymentPlanSummary
      :summary="formattedSummary"
      @clear="openClearConfirmModal"
      @new-payment-plan="openPaymentPlanModal"
    />

    <SalePaymentPlanToolbar
      :search="search"
      :billed-filter="billedFilter"
      :show-customer-filter="showCustomerFilter"
      :customer-filter="customerFilter"
      :customer-options="resolvedCustomerOptions"
      :date-range-value="dateRangeValue"
      :date-range-presets="dateRangePresets"
      @update:search="search = $event"
      @update:billedFilter="billedFilter = $event"
      @update:customerFilter="customerFilter = $event"
      @update:dateRangeValue="dateRangeValue = $event"
    />

    <StatusTabs
      :tabs="statusTabs"
      :active-tab="activeStatusTab"
      @select="selectStatusTab"
    />

    <SalePaymentPlanTable
      :rows="normalizedRows"
      :loading="loading"
      :show-customer-column="showCustomerFilter"
      :context-sale-id="saleId"
      :context-sale-code="saleCode"
      :context-sale-name="saleName"
      @pay-plan="handlePayPlan"
      @edit-plan="handleEditPlan"
      @delete-plan="handleDeletePlan"
      @invoice-plan="handleInvoicePlan"
      @simple-note-plan="handleSimpleNotePlan"
      @duplicate-plan="handleDuplicatePlan"
      @credit-note="handleCreditNote"
    />

    <PaymentPlanModal
      :show="isPaymentPlanModalOpen"
      :mode="paymentPlanModalMode"
      :initial-data="editingPaymentPlan"
      :sale-id="saleId"
      :show-customer-selector="showCustomerFilter"
      :customer-options="resolvedCustomerOptions"
      :initial-customer-id="modalInitialCustomerId"
      :sale-price="salePrice"
      :existing-planned-total="existingPlannedTotal"
      :submitting="isPaymentPlanSubmitting"
      @close="closePaymentPlanModal"
      @submit="submitPaymentPlan"
    />

    <PayModal
      :show="isPayPlanModalOpen"
      :row="payingPlan"
      :submitting="isPayPlanSubmitting"
      @close="closePayPlanModal"
      @submit="submitPayPlan"
    />
  </section>
</template>

<script>
import PaymentPlanModal from '@/components/sales/shared/paymentPlan/PaymentPlanModal.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import SalePaymentPlanSummary from '@/components/sales/shared/paymentPlan/SalePaymentPlanSummary.vue';
import SalePaymentPlanToolbar from '@/components/sales/shared/paymentPlan/SalePaymentPlanToolbar.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import SalePaymentPlanTable from '@/components/sales/shared/paymentPlan/SalePaymentPlanTable.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import paymentPlansService from '@/services/paymentPlans';
import paymentRepository from '@/services/paymentRepository';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import {
  extractApiErrorMessage,
  extractPayload,
  extractApiSuccessMessage,
  extractCollection,
} from '@/utils/dataNormalization';
import { formatDateForApi } from '@/utils/datePresentation';
import { formatCurrency } from '@/utils/numberFormat';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  normalizePaymentPlanRow,
  normalizePaymentPlanStatusCode,
} from '@/composables/paymentPlans/usePaymentPlanNormalization';

const { catalog: text } = useDomainText('payments');

const sortPaymentPlansByDateAsc = (rows = []) => {
  return [...rows].sort((left, right) => {
    const leftTime = new Date(left?.date || 0).getTime();
    const rightTime = new Date(right?.date || 0).getTime();

    if (leftTime === rightTime) {
      return Number(left?.id || 0) - Number(right?.id || 0);
    }

    return leftTime - rightTime;
  });
};

const createEmptySummarySection = () => ({
  subtotal: 0,
  transferred_taxes: 0,
  withheld_taxes: 0,
  total: 0,
});

const normalizeSummarySection = (section = null) => {
  const source = section && typeof section === 'object' ? section : {};
  const subtotal = Number(source?.subtotal || 0) || 0;
  const transferredTaxes = Number(source?.transferred_taxes ?? source?.transfered_taxes ?? source?.transferredTaxes ?? 0) || 0;
  const withheldTaxes = Number(source?.withheld_taxes ?? source?.withheldTaxes ?? 0) || 0;
  const explicitTotal = Number(source?.total || 0) || 0;

  return {
    subtotal,
    transferred_taxes: transferredTaxes,
    withheld_taxes: withheldTaxes,
    total: explicitTotal || (subtotal + transferredTaxes - withheldTaxes),
  };
};

const normalizePaymentPlanSummary = (summary = null) => {
  if (!summary || typeof summary !== 'object') {
    return null;
  }

  return {
    payment_plan: normalizeSummarySection(summary?.payment_plan ?? summary?.paymentPlan),
    paid: normalizeSummarySection(summary?.paid),
    pending: normalizeSummarySection(summary?.pending),
  };
};

const buildPaymentPlanSummaryFromRows = (rows = []) => {
  const summary = {
    payment_plan: createEmptySummarySection(),
    paid: createEmptySummarySection(),
    pending: createEmptySummarySection(),
  };

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const subtotal = Number(row?.subtotal || 0) || 0;
    const transferredTaxes = Number(row?.taxes?.transferred_total ?? row?.transfered_taxes ?? row?.transferred_taxes ?? 0) || 0;
    const withheldTaxes = Number(row?.taxes?.withheld_total ?? row?.withheld_taxes ?? 0) || 0;
    const total = Number(row?.total || (subtotal + transferredTaxes - withheldTaxes)) || 0;
    const statusCode = normalizePaymentPlanStatusCode(row?.payment_status_code);
    const bucketKey = statusCode === 'DONE' ? 'paid' : 'pending';

    summary.payment_plan.subtotal += subtotal;
    summary.payment_plan.transferred_taxes += transferredTaxes;
    summary.payment_plan.withheld_taxes += withheldTaxes;
    summary.payment_plan.total += total;

    summary[bucketKey].subtotal += subtotal;
    summary[bucketKey].transferred_taxes += transferredTaxes;
    summary[bucketKey].withheld_taxes += withheldTaxes;
    summary[bucketKey].total += total;
  });

  return summary;
};

const formatSummarySection = (section = {}) => {
  const normalized = normalizeSummarySection(section);

  return {
    subtotal: formatCurrency(normalized.subtotal),
    transferred_taxes: formatCurrency(normalized.transferred_taxes),
    withheld_taxes: formatCurrency(normalized.withheld_taxes),
    total: formatCurrency(normalized.total),
  };
};

export default {
  name: 'SalePaymentPlanTab',
  components: {
    PaymentPlanModal,
    PayModal,
    SalePaymentPlanSummary,
    SalePaymentPlanToolbar,
    StatusTabs,
    SalePaymentPlanTable,
    ConfirmActionModal,
  },
  props: {
    sale: {
      type: Object,
      default: null,
    },
    showCustomerFilter: {
      type: Boolean,
      default: false,
    },
    initialCustomerId: {
      type: Number,
      default: 0,
    },
    openModalRequestId: {
      type: Number,
      default: 0,
    },
  },
  emits: ['payment-plan-saved', 'invoice-plan'],
  data() {
    const initialCustomerId = Number(this.initialCustomerId || 0) || 0;
    const initialCustomerFilter = this.showCustomerFilter && initialCustomerId > 0
      ? String(initialCustomerId)
      : 'all';

    return {
      text,
      search: '',
      billedFilter: 'all',
      customerFilter: initialCustomerFilter,
      activeStatusTab: 'all',
      dateRangeValue: null,
      dateRangePresets: Array.isArray(text.toolbar.dateRangePresets)
        ? [...text.toolbar.dateRangePresets]
        : [],
      isPaymentPlanModalOpen: false,
      isPaymentPlanSubmitting: false,
      loading: false,
      paymentPlanRows: [],
      paymentPlanSummary: null,
      paymentPlanModalMode: 'create',
      editingPaymentPlan: null,
      customerOptions: [],
      lastFetchKey: '',
      activeFetchToken: 0,
      isClearConfirmModalOpen: false,
      isClearingPaymentPlans: false,
      isDeleteConfirmModalOpen: false,
      isDeletingPaymentPlan: false,
      deletingPlan: null,
      isPayPlanModalOpen: false,
      isPayPlanSubmitting: false,
      payingPlan: null,
      lastHandledOpenModalRequestId: 0,
    };
  },
  computed: {
    saleId() {
      return Number(this.sale?.id || this.sale?.sale_id || 0);
    },
    saleName() {
      return String(this.sale?.name || this.sale?.sale_name || '').trim();
    },
    saleCode() {
      return String(this.sale?.code || this.sale?.sale_code || '').trim();
    },
    requestParams() {
      const range = Array.isArray(this.dateRangeValue) ? this.dateRangeValue : [];
      const start = formatDateForApi(range[0]);
      const end = formatDateForApi(range[1]);
      const customerId = Number(this.customerFilter || 0) || 0;

      const customerFilters = this.showCustomerFilter && customerId > 0
        ? {
          customer: customerId,
          customer_id: customerId,
        }
        : {};

      return {
        sale_id: this.saleId,
        billed: this.billedFilter,
        date_start: start,
        date_end: end,
        ...customerFilters,
      };
    },
    requestParamsKey() {
      return JSON.stringify(this.requestParams || {});
    },
    sourceRows() {
      return this.paymentPlanRows;
    },
    filteredRows() {
      const query = String(this.search || '').trim().toLowerCase();

      return this.sourceRows.filter((row) => {
        if (this.activeStatusTab === 'pending' && normalizePaymentPlanStatusCode(row.payment_status_code) !== 'PENDING') {
          return false;
        }

        if (this.activeStatusTab === 'paid' && normalizePaymentPlanStatusCode(row.payment_status_code) !== 'DONE') {
          return false;
        }

        if (query === '') {
          return true;
        }

        return [row.concept, row.invoice, row.simple_note_reference, row.payment]
          .some((value) => String(value || '').trim().toLowerCase().includes(query));
      });
    },
    normalizedRows() {
      return this.filteredRows;
    },
    statusTabs() {
      const rows = this.sourceRows;
      const pending = rows.filter((row) => normalizePaymentPlanStatusCode(row.payment_status_code) === 'PENDING').length;
      const paid = rows.filter((row) => normalizePaymentPlanStatusCode(row.payment_status_code) === 'DONE').length;

      return [
        { key: 'all', label: this.text.tab.statusTabs.all, count: rows.length, showCount: true },
        { key: 'pending', label: this.text.tab.statusTabs.pending, count: pending, showCount: true },
        { key: 'paid', label: this.text.tab.statusTabs.paid, count: paid, showCount: true },
      ];
    },
    salePrice() {
      return Number(this.sale?.sale_price || this.sale?.subtotal || 0) || 0;
    },
    existingPlannedTotal() {
      return this.sourceRows.reduce((total, row) => total + (Number(row?.subtotal || 0) || 0), 0);
    },
    modalInitialCustomerId() {
      const selectedCustomerId = Number(this.customerFilter || 0) || 0;
      if (selectedCustomerId > 0) {
        return selectedCustomerId;
      }

      return Number(this.initialCustomerId || 0) || 0;
    },
    saleCustomerOptions() {
      const rawCustomers = Array.isArray(this.sale?.customers) ? this.sale.customers : [];
      return normalizeCatalogOptions(rawCustomers, { useCodeInLabel: false });
    },
    resolvedCustomerOptions() {
      return this.customerOptions.length > 0
        ? this.customerOptions
        : this.saleCustomerOptions;
    },
    formattedSummary() {
      const resolvedSummary = this.paymentPlanSummary || buildPaymentPlanSummaryFromRows(this.sourceRows);

      return {
        payment_plan: formatSummarySection(resolvedSummary?.payment_plan),
        paid: formatSummarySection(resolvedSummary?.paid),
        pending: formatSummarySection(resolvedSummary?.pending),
      };
    },
    clearConfirmMessage() {
      return this.text.tab.confirms.clear.message;
    },
    deleteConfirmMessage() {
      const row = this.deletingPlan;
      if (!row || typeof row !== 'object') {
        return this.text.tab.confirms.deleteMessageDefault;
      }

      const customer = String(row?.customer_name || row?.company_name || '').trim() || this.text.tab.confirms.noCustomer;
      const concept = String(row?.concept || '').trim() || this.text.tab.confirms.noConcept;
      const amount = formatCurrency(Number(row?.total ?? row?.subtotal ?? 0) || 0);

      return this.text.tab.confirms.deleteSummary({ customer, concept, amount });
    },
    deleteConfirmText() {
      return this.isDeletingPaymentPlan
        ? this.text.tab.confirms.delete.confirming
        : this.text.tab.confirms.delete.confirm;
    },
  },
  watch: {
    requestParamsKey: {
      immediate: true,
      handler() {
        this.fetchPaymentPlans();
      },
    },
    initialCustomerId(nextValue) {
      const customerId = Number(nextValue || 0) || 0;
      if (!this.showCustomerFilter || customerId <= 0) {
        return;
      }

      const nextFilter = String(customerId);
      const allowedValues = new Set(
        this.resolvedCustomerOptions
          .map((option) => String(option?.value || '').trim())
          .filter((value) => value !== '')
      );

      if (!allowedValues.has(nextFilter)) {
        this.customerFilter = 'all';
        return;
      }

      if (this.customerFilter === nextFilter) {
        return;
      }

      this.customerFilter = nextFilter;
    },
    saleId: {
      immediate: true,
      handler() {
        this.ensureCustomerOptionsLoaded();
      },
    },
    resolvedCustomerOptions: {
      immediate: true,
      deep: true,
      handler(options) {
        this.sanitizeCustomerFilter(options);
      },
    },
    openModalRequestId: {
      immediate: true,
      handler(nextValue) {
        const requestId = Number(nextValue || 0) || 0;
        if (requestId <= 0 || requestId === this.lastHandledOpenModalRequestId) {
          return;
        }

        this.lastHandledOpenModalRequestId = requestId;
        this.openPaymentPlanModal();
      },
    },
  },
  methods: {
    sanitizeCustomerFilter(options = this.resolvedCustomerOptions) {
      if (!this.showCustomerFilter) {
        this.customerFilter = 'all';
        return;
      }

      const allowedValues = new Set(
        (Array.isArray(options) ? options : [])
          .map((option) => String(option?.value || '').trim())
          .filter((value) => value !== '')
      );

      const selectedValue = String(this.customerFilter || 'all').trim() || 'all';
      if (selectedValue === 'all') {
        return;
      }

      if (!allowedValues.has(selectedValue)) {
        this.customerFilter = 'all';
      }
    },
    async ensureCustomerOptionsLoaded(force = false) {
      if (!this.showCustomerFilter) {
        this.customerOptions = [];
        this.sanitizeCustomerFilter([]);
        return;
      }

      const saleOptions = this.saleCustomerOptions;
      if (!force && saleOptions.length > 0) {
        this.customerOptions = saleOptions;
        this.sanitizeCustomerFilter(saleOptions);
        return;
      }

      if (this.saleId <= 0) {
        this.customerOptions = [];
        this.sanitizeCustomerFilter([]);
        return;
      }

      try {
        const response = await salesMultiCustomerService.customers(this.saleId, {}, { skipLoading: true });
        const rows = extractCollection(response);
        this.customerOptions = normalizeCatalogOptions(rows, { useCodeInLabel: false });
        this.sanitizeCustomerFilter(this.customerOptions);
      } catch (error) {
        this.customerOptions = [];
        this.sanitizeCustomerFilter([]);
      }
    },
    async fetchPaymentPlans(force = false) {
      if (this.saleId <= 0) {
        this.paymentPlanRows = [];
        this.paymentPlanSummary = null;
        this.loading = false;
        this.lastFetchKey = '';
        return;
      }

      const fetchKey = this.requestParamsKey;
      if (!force && fetchKey === this.lastFetchKey) {
        return;
      }

      this.lastFetchKey = fetchKey;
      const token = ++this.activeFetchToken;

      this.loading = true;
      try {
        const response = await paymentPlansService.index(this.requestParams);
        const payload = extractPayload(response);
        const payloadSummary = payload && typeof payload === 'object'
          ? payload.summary
          : null;
        const rows = extractCollection(response);
        if (token === this.activeFetchToken) {
          const normalizedRows = rows.map((row, index) => normalizePaymentPlanRow(row, index));
          this.paymentPlanRows = sortPaymentPlansByDateAsc(normalizedRows);
          this.paymentPlanSummary = normalizePaymentPlanSummary(payloadSummary);
        }
      } catch (error) {
        if (token === this.activeFetchToken) {
          this.paymentPlanRows = [];
          this.paymentPlanSummary = null;
        }
      } finally {
        if (token === this.activeFetchToken) {
          this.loading = false;
        }
      }
    },
    async openPaymentPlanModal() {
      await this.ensureCustomerOptionsLoaded(true);
      this.paymentPlanModalMode = 'create';
      this.editingPaymentPlan = null;
      this.isPaymentPlanModalOpen = true;
    },
    closePaymentPlanModal(force = false) {
      if (!force && this.isPaymentPlanSubmitting) {
        return;
      }

      this.isPaymentPlanModalOpen = false;
    },
    async submitPaymentPlan(payload) {
      this.isPaymentPlanSubmitting = true;

      try {
        const requestPayload = {
          ...payload,
          sale_id: this.saleId,
        };

        if (this.paymentPlanModalMode === 'edit' && Number(this.editingPaymentPlan?.id || 0) > 0) {
          const response = await paymentPlansService.update(Number(this.editingPaymentPlan.id), requestPayload);
          const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.updatePlan);
          if (apiErrorMessage) {
            this.$toast?.error(apiErrorMessage);
            return;
          }
          this.$toast?.success(this.text.tab.success.updatePlan);
        } else {
          const response = await paymentPlansService.store(requestPayload);
          const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.createPlan);
          if (apiErrorMessage) {
            this.$toast?.error(apiErrorMessage);
            return;
          }
          this.$toast?.success(this.text.tab.success.createPlan);
        }

        this.closePaymentPlanModal(true);
        this.paymentPlanModalMode = 'create';
        this.editingPaymentPlan = null;

        this.$emit('payment-plan-saved');

        try {
          await this.fetchPaymentPlans(true);
        } catch ($unusedRefreshError) {
          // Keep modal closed even if list refresh fails.
        }
      } catch (error) {
        this.$toast?.error(this.text.tab.errors.savePlan);
      } finally {
        this.isPaymentPlanSubmitting = false;
      }
    },
    openClearConfirmModal() {
      this.isClearConfirmModalOpen = true;
    },
    closeClearConfirmModal() {
      if (this.isClearingPaymentPlans) {
        return;
      }

      this.isClearConfirmModalOpen = false;
    },
    async confirmClearPaymentPlans() {
      const saleId = Number(this.saleId || 0);
      if (saleId <= 0 || this.isClearingPaymentPlans) {
        this.closeClearConfirmModal();
        return;
      }

      this.isClearingPaymentPlans = true;
      try {
        const response = await paymentPlansService.clearBySale(saleId);
        const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.clearPlans);
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.tab.success.clearPlans));
        this.isClearConfirmModalOpen = false;
        await this.fetchPaymentPlans(true);
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.tab.errors.clearPlans);
      } finally {
        this.isClearingPaymentPlans = false;
      }
    },
    selectStatusTab(tabKey) {
      this.activeStatusTab = String(tabKey || '').trim() || 'all';
    },
    handlePayPlan(row) {
      this.payingPlan = row && typeof row === 'object' ? { ...row } : null;
      this.isPayPlanModalOpen = true;
    },
    closePayPlanModal() {
      if (this.isPayPlanSubmitting) {
        return;
      }

      this.isPayPlanModalOpen = false;
      this.payingPlan = null;
    },
    async submitPayPlan(payload) {
      this.isPayPlanSubmitting = true;

      try {
        const paymentPlanId = Number(payload?.payment_plan_id || 0);
        if (paymentPlanId <= 0) {
          this.$toast?.error(this.text.tab.toasts.errorUnknownPlanToPay);
          return;
        }

        const response = await paymentRepository.payPlan(paymentPlanId, payload);
        const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.payPlan);
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        const amount = formatCurrency(Number(payload?.amount || 0) || 0);
        this.$toast?.success(extractApiSuccessMessage(response, this.text.tab.toasts.successPayPlan({ amount })));
        this.closePayPlanModal();
        await this.fetchPaymentPlans(true);
        this.$emit('payment-plan-saved');
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.tab.errors.payPlan);
      } finally {
        this.isPayPlanSubmitting = false;
      }
    },
    async handleEditPlan(row) {
      const planId = Number(row?.id || 0);
      if (planId <= 0) {
        return;
      }

      try {
        const response = await paymentPlansService.show(planId);
        const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.loadPlanDetail);
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        const payload = extractPayload(response);
        const detail = payload && typeof payload?.data === 'object'
          ? payload.data
          : payload;
        this.paymentPlanModalMode = 'edit';
        this.editingPaymentPlan = detail && typeof detail === 'object'
          ? normalizePaymentPlanRow(detail)
          : normalizePaymentPlanRow(row && typeof row === 'object' ? { ...row } : null);
        this.isPaymentPlanModalOpen = true;
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.tab.errors.loadPlan);
      }
    },
    async handleDuplicatePlan(row) {
      const planId = Number(row?.id || 0);
      if (planId <= 0) {
        return;
      }

      try {
        const response = await paymentPlansService.show(planId);
        const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.loadPlanDetail);
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        const payload = extractPayload(response);
        const detail = payload && typeof payload?.data === 'object'
          ? payload.data
          : payload;

        const normalizedCloneSource = detail && typeof detail === 'object'
          ? normalizePaymentPlanRow(detail)
          : normalizePaymentPlanRow(row && typeof row === 'object' ? { ...row } : null);

        const clonedPlan = {
          ...normalizedCloneSource,
          id: 0,
          invoice_id: 0,
          invoice: '',
          invoiced: false,
          invoice_date: '',
          invoice_payment_method: '',
          invoice_payment_method_label: '',
          invoice_contributor_id: 0,
          invoice_contributor_name: '',
          invoice_billed_rfc: '',
          invoice_billing_data: {
            payment_method: { code: '', label: '' },
            contributor: { id: 0, name: '', rfc: '' },
            rfc: '',
          },
          simple_note_id: 0,
          simple_note_reference: '',
          simple_note_date: '',
          payment: '',
          payment_status_code: 'PENDING',
          payment_status: '',
          edit_mode: 'full',
          edit_permissions: {
            can_edit_general: true,
            can_edit_billing: true,
            can_edit_amount: true,
            can_edit_payment_date: true,
            can_edit_details: true,
          },
        };

        this.paymentPlanModalMode = 'create';
        this.editingPaymentPlan = clonedPlan;

        await this.ensureCustomerOptionsLoaded(true);
        this.isPaymentPlanModalOpen = true;
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.tab.errors.loadPlan);
      }
    },
    handleDeletePlan(row) {
      const planId = Number(row?.id || 0);
      if (planId <= 0) {
        return;
      }

      this.deletingPlan = row && typeof row === 'object' ? { ...row } : null;
      this.isDeleteConfirmModalOpen = true;
    },
    closeDeleteConfirmModal() {
      if (this.isDeletingPaymentPlan) {
        return;
      }

      this.isDeleteConfirmModalOpen = false;
      this.deletingPlan = null;
    },
    async confirmDeletePlan() {
      const planId = Number(this.deletingPlan?.id || 0);
      if (planId <= 0 || this.isDeletingPaymentPlan) {
        this.closeDeleteConfirmModal();
        return;
      }

      this.isDeletingPaymentPlan = true;
      try {
        const response = await paymentPlansService.delete(planId);
        const apiErrorMessage = extractApiErrorMessage(response, this.text.tab.errors.deletePlan);
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.tab.success.deletePlan));
        this.isDeleteConfirmModalOpen = false;
        this.deletingPlan = null;
        await this.fetchPaymentPlans(true);
        this.$emit('payment-plan-saved');
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.tab.errors.deletePlan);
      } finally {
        this.isDeletingPaymentPlan = false;
      }
    },
    handleInvoicePlan(row) {
      this.$emit('invoice-plan', row && typeof row === 'object' ? { ...row } : row);
    },
    handleSimpleNotePlan(row) {
      const paymentId = Number(row?.id || 0) || 0;
      if (paymentId <= 0) {
        this.$toast?.error(this.text.tab.toasts.errorUnknownPlanToPay);
        return;
      }

      const customerId = Number(row?.company_id || row?.customer_id || 0) || 0;
      this.$router.push({
        name: 'simpleNoteCreate',
        query: {
          payment_id: String(paymentId),
          ...(customerId > 0 ? { customer_id: String(customerId) } : {}),
        },
      });
    },
    handleCreditNote(row) {
      this.$toast?.info(this.text.tab.toasts.infoCreditNote({ id: row?.id || '' }));
    },
  },
};
</script>

<style scoped>
.sale-payment-plan-tab {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
